import React from "react";
import { motion } from "framer-motion";
import { Mail, MapPin, Phone, Rocket } from "lucide-react";

export default function Contact() {
    return (
        <div className="bg-gray-50 min-h-screen">
            {/* Header - Matching Services Page style */}
            <section className="relative py-24 px-6 overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900"></div>
                <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-blue-900/20 to-transparent"></div>
                <div className="absolute bottom-0 left-0 w-1/2 h-full bg-gradient-to-r from-purple-900/20 to-transparent"></div>

                <div className="max-w-4xl mx-auto text-center relative z-10">
                    <motion.span
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="text-blue-400 font-semibold tracking-wider uppercase text-sm"
                    >
                        Contact Us
                    </motion.span>
                    <motion.h1
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="text-5xl md:text-6xl font-extrabold text-white mt-4 mb-6 tracking-tight"
                    >
                        Get in Touch
                    </motion.h1>
                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.1 }}
                        className="text-xl text-slate-300 max-w-2xl mx-auto font-light leading-relaxed"
                    >
                        Whether you have a question about our platform, need a demo, or discuss a partnership, we are here to help.
                    </motion.p>
                </div>
            </section>

            <div className="max-w-6xl mx-auto px-6 py-20">
                <div className="flex items-center gap-4 mb-16">
                    <div className="h-px bg-slate-300 flex-1"></div>
                    <h2 className="text-3xl font-bold text-slate-800 uppercase tracking-widest text-center px-4">Connect With Us</h2>
                    <div className="h-px bg-slate-300 flex-1"></div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-start">
                    {/* Contact Info Card */}
                    <div className="bg-white p-10 rounded-2xl shadow-md border border-gray-200 h-full">
                        <div className="flex items-center gap-4 mb-8">
                            <div className="p-4 bg-blue-600 text-white rounded-xl shadow-sm">
                                <Phone size={28} />
                            </div>
                            <h2 className="text-3xl font-bold text-gray-900">Contact Information</h2>
                        </div>

                        <div className="space-y-6">
                            <div className="flex items-start gap-4 p-6 bg-slate-50 rounded-2xl border border-slate-100 hover:border-blue-300 transition-colors">
                                <div className="p-3 bg-blue-100 shadow-sm rounded-xl text-blue-600">
                                    <MapPin size={24} />
                                </div>
                                <div>
                                    <h3 className="font-semibold text-gray-900 mb-2">Visit Us</h3>
                                    <p className="text-slate-600 text-sm leading-relaxed">
                                        TF-02, Third Floor, Sun Twilight Metro Street,<br />
                                        Delta-1 Metro Station, Sector -27<br />
                                        Greater Noida-201308
                                    </p>
                                </div>
                            </div>

                            <div className="flex items-start gap-4 p-6 bg-slate-50 rounded-2xl border border-slate-100 hover:border-purple-300 transition-colors">
                                <div className="p-3 bg-purple-100 shadow-sm rounded-xl text-purple-600">
                                    <Mail size={24} />
                                </div>
                                <div>
                                    <h3 className="font-semibold text-gray-900 mb-2">Email Us</h3>
                                    <a href="mailto:info@smartreceptors.com" className="text-slate-600 text-sm hover:text-blue-600 transition-colors">
                                        info@smartreceptors.com
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Connect Card (Replaced Form) */}
                    <div className="bg-white p-10 rounded-2xl shadow-md border border-gray-200 text-center flex flex-col justify-center items-center h-full">
                        <div className="p-4 bg-purple-600 text-white rounded-xl shadow-sm mb-6">
                            <Rocket size={28} />
                        </div>
                        <h3 className="text-2xl font-bold text-gray-900 mb-4">Ready to Transform?</h3>
                        <p className="text-gray-600 mb-8 max-w-sm">
                            Connect with our experts to discuss how AI can accelerate your chemical research and development.
                        </p>
                        <a
                            href="https://www.linkedin.com/in/pkacker/"
                            target="_blank"
                            rel="noreferrer"
                            className="px-8 py-4 bg-[#0A66C2] text-white rounded-xl font-semibold hover:bg-[#004182] transition-colors shadow-sm flex items-center gap-3"
                        >
                            <svg className="w-6 h-6 fill-current" viewBox="0 0 24 24">
                                <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                            </svg>
                            Connect on LinkedIn
                        </a>
                    </div>
                </div>
            </div>
        </div>
    );
}
